/**
 * Tầng 2/2: điều phối `computeIndicatorComputationResult`.
 *
 * Trước split 2026-09-30 hàm này chiếm 877 dòng vì chứa thân của cả 60 khối chỉ báo.
 * Nay mỗi nhóm chỉ báo nằm ở module riêng (`computeMovingAverage`, `computeMomentum`,
 * `computeVolatility`, `computeTrend`, `computeVolumeFlow`, `computeMarketStructure`,
 * `computeProfile`) và hàm này chỉ còn: gọi từng nhóm, ghép `valuesByKey`, rồi trả về.
 *
 * HAI THỨ TUYỆT ĐỐI KHÔNG ĐƯỢC ĐỔI Ở ĐÂY
 * 1. Danh sách field của `intermediateResult` và của `return` phải giữ nguyên thứ tự
 *    và thứ tự khóa — UI và `valuesByKey` đọc theo thứ tự này.
 * 2. Vòng lặp `orderedSeries` phải chạy hai lần: lượt 1 tính mọi series gốc, lượt 2
 *    tính series con (indicator-on-indicator) sau khi đã có giá trị của cha.
 */
import { bollingerSeries, demaSeries, emaSeries, hmaSeries, rsiSeries, smaSeries, temaSeries, wmaSeries, } from "../../indicators/utils";
import { buildIndicatorSeriesKey } from "../seriesValueResolver";
import { normalizeSeriesTypeId } from "../types/pane-descriptor";
import { getPineScriptAdapter } from "../../pinescript/adapter";
import { blankUntil, mergeBand, numberParam } from "./computationHelpers";
import { computeMarketStructureBlocks } from "./computeMarketStructure";
import { computeMomentumBlocks } from "./computeMomentum";
import { computeMovingAverageBlocks } from "./computeMovingAverage";
import { computeProfileBlocks } from "./computeProfile";
import { computeTrendBlocks } from "./computeTrend";
import { computeVolatilityBlocks } from "./computeVolatility";
import { computeVolumeFlowBlocks } from "./computeVolumeFlow";
import { materializeIndicatorValues } from "./materializeIndicatorValues";
export function computeIndicatorComputationResult(raw, plan, options) {
    const closes = raw.map((bar) => bar.close);
    const { emaByPeriod, smaByPeriod, macdByKey, bollingerByKey, wmaByPeriod, vwmaByPeriod, hmaByPeriod, demaByPeriod, temaByPeriod, maRibbonByKey, } = computeMovingAverageBlocks(raw, closes, plan);
    const { rsiByPeriod, wrByPeriod, vrByPeriod, kdjByKey, cciByPeriod, dmiByPeriod, biasByPeriod, brarByPeriod, mtmByKey, emvByPeriod, ao, rocByPeriod, trixByKey, psyByKey, crByKey, ac, smfi, } = computeMomentumBlocks(raw, closes, plan);
    const { bbi, sarByKey, keltnerByKey, donchianByPeriod, chaikinVolByPeriod, stdDevByPeriod, hvByKey, ciByPeriod, supertrendByKey, } = computeVolatilityBlocks(raw, closes, plan);
    const { dmaByKey, ichimokuByKey, alligatorByKey, fractals, gatorByKey } = computeTrendBlocks(raw, plan);
    const { obv, pvt, cmfByPeriod, mfiByPeriod, volOscByKey, chaikinOscByKey, vwapByKey, anchoredVwapByKey, cvd, strength, strengthRelativeByKey, whaleByKey, } = computeVolumeFlowBlocks(raw, plan, options);
    const { pivotStandardByKey, pivotHighLowByKey, wyckoffWaveByKey } = computeMarketStructureBlocks(raw, plan);
    const { marketProfileByKey, volumeProfileByKey, footprintByKey, deltaProfileByKey } = computeProfileBlocks(raw, plan, options);
    const valuesByKey = new Map();
    const intermediateResult = {
        emaByPeriod,
        smaByPeriod,
        rsiByPeriod,
        macdByKey,
        bollingerByKey,
        whaleByKey,
        bbi,
        sarByKey,
        obv,
        wrByPeriod,
        vrByPeriod,
        kdjByKey,
        cciByPeriod,
        dmiByPeriod,
        biasByPeriod,
        brarByPeriod,
        mtmByKey,
        emvByPeriod,
        ao,
        rocByPeriod,
        trixByKey,
        dmaByKey,
        pvt,
        psyByKey,
        crByKey,
        cvd,
        strength,
        wmaByPeriod,
        vwmaByPeriod,
        hmaByPeriod,
        demaByPeriod,
        temaByPeriod,
        maRibbonByKey,
        ichimokuByKey,
        supertrendByKey,
        keltnerByKey,
        donchianByPeriod,
        chaikinVolByPeriod,
        stdDevByPeriod,
        hvByKey,
        ciByPeriod,
        cmfByPeriod,
        mfiByPeriod,
        volOscByKey,
        chaikinOscByKey,
        vwapByKey,
        anchoredVwapByKey,
        alligatorByKey,
        fractals,
        gatorByKey,
        ac,
        pivotStandardByKey,
        pivotHighLowByKey,
        marketProfileByKey,
        volumeProfileByKey,
        footprintByKey,
        deltaProfileByKey,
        strengthRelativeByKey,
        smfi,
        wyckoffWaveByKey,
    };
    for (const series of plan.orderedSeries) {
        const key = buildIndicatorSeriesKey(series);
        if (!key)
            continue;
        if (valuesByKey.has(key)) {
            continue;
        }
        if (series.sourceType === "indicator" && series.sourceId) {
            continue;
        }
        const plugin = getPineScriptAdapter()?.getPinePlugin(series.type);
        if (plugin) {
            const output = plugin.compute({ bars: raw }, series.params || {});
            const keys = Object.keys(output);
            const values = raw.map((_, idx) => {
                if (keys.length === 1) {
                    return output[keys[0]][idx];
                }
                const obj = {};
                for (const k of keys) {
                    obj[k] = output[k][idx];
                }
                return obj;
            });
            valuesByKey.set(key, values);
            continue;
        }
        const arr = raw.map((_, index) => {
            const values = materializeIndicatorValues(index, plan, intermediateResult, raw);
            return values[key];
        });
        valuesByKey.set(key, arr);
    }
    const getSubValue = (v, key) => {
        if (v === undefined || v === null || Number.isNaN(v))
            return undefined;
        if (typeof v === "object" && key) {
            const sub = v[key];
            return typeof sub === "number" && !Number.isNaN(sub) ? sub : undefined;
        }
        return typeof v === "number" && !Number.isNaN(v) ? v : undefined;
    };
    for (const series of plan.orderedSeries) {
        const key = buildIndicatorSeriesKey(series);
        if (!key)
            continue;
        if (valuesByKey.has(key)) {
            continue;
        }
        if (series.sourceType === "indicator" && series.sourceId) {
            const parent = plan.orderedSeries.find(s => s.id === series.sourceId);
            if (!parent)
                continue;
            const parentKey = buildIndicatorSeriesKey(parent);
            if (!parentKey)
                continue;
            const parentValues = valuesByKey.get(parentKey);
            if (!parentValues)
                continue;
            const inputValues = parentValues.map(v => getSubValue(v, series.sourceKey));
            let childResult;
            if (series.type === "EMA") {
                const period = numberParam(series, "period", 20);
                childResult = blankUntil(emaSeries(inputValues, period), Math.max(period - 1, 0));
            }
            else if (series.type === "MA") {
                const period = numberParam(series, "period", 20);
                childResult = blankUntil(smaSeries(inputValues, period), Math.max(period - 1, 0));
            }
            else if (series.type === "RSI") {
                const period = numberParam(series, "period", 14);
                childResult = blankUntil(rsiSeries(inputValues, period), Math.max(period, 1));
            }
            else if (normalizeSeriesTypeId(series.type) === "BollingerBand") {
                const period = numberParam(series, "period", 20);
                const stdDev = numberParam(series, "stdDev", 2);
                const band = bollingerSeries(inputValues, period, stdDev);
                childResult = mergeBand(band.upper, band.middle, band.lower, Math.max(period - 1, 0));
            }
            else if (series.type === "WMA") {
                const period = numberParam(series, "period", 9);
                childResult = blankUntil(wmaSeries(inputValues, period), Math.max(period - 1, 0));
            }
            else if (series.type === "HMA") {
                const period = numberParam(series, "period", 9);
                childResult = blankUntil(hmaSeries(inputValues, period), Math.max(period - 1, 0));
            }
            else if (series.type === "DEMA") {
                const period = numberParam(series, "period", 9);
                childResult = blankUntil(demaSeries(inputValues, period), Math.max(period - 1, 0));
            }
            else if (series.type === "TEMA") {
                const period = numberParam(series, "period", 9);
                childResult = blankUntil(temaSeries(inputValues, period), Math.max(period - 1, 0));
            }
            if (childResult) {
                valuesByKey.set(key, childResult);
            }
        }
    }
    return {
        valuesByKey,
        emaByPeriod,
        smaByPeriod,
        rsiByPeriod,
        macdByKey,
        bollingerByKey,
        whaleByKey,
        bbi,
        sarByKey,
        obv,
        wrByPeriod,
        vrByPeriod,
        kdjByKey,
        cciByPeriod,
        dmiByPeriod,
        biasByPeriod,
        brarByPeriod,
        mtmByKey,
        emvByPeriod,
        ao,
        rocByPeriod,
        trixByKey,
        dmaByKey,
        pvt,
        psyByKey,
        crByKey,
        cvd,
        strength,
        wmaByPeriod,
        vwmaByPeriod,
        hmaByPeriod,
        demaByPeriod,
        temaByPeriod,
        maRibbonByKey,
        ichimokuByKey,
        supertrendByKey,
        keltnerByKey,
        donchianByPeriod,
        chaikinVolByPeriod,
        stdDevByPeriod,
        hvByKey,
        ciByPeriod,
        cmfByPeriod,
        mfiByPeriod,
        volOscByKey,
        chaikinOscByKey,
        vwapByKey,
        anchoredVwapByKey,
        alligatorByKey,
        fractals,
        gatorByKey,
        ac,
        pivotStandardByKey,
        pivotHighLowByKey,
        marketProfileByKey,
        volumeProfileByKey,
        footprintByKey,
        deltaProfileByKey,
        strengthRelativeByKey,
        smfi,
        wyckoffWaveByKey,
    };
}
